import { useState, useRef } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertCircle, ClipboardList, Clock, Plus, ImagePlus, X, MessageSquare, Loader2, Upload, Image } from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { apiRequest, queryClient } from "@/lib/queryClient";

interface PendingPhoto {
  file: File;
  preview: string;
}

interface Claim {
  id: string;
  claimNumber: string;
  orderId: string;
  orderItemId: string | null;
  issueType: string;
  description: string;
  status: string;
  resolutionNotes: string | null;
  resolutionType: string | null;
  refundAmount: string | null;
  createdAt: string;
  reviewedAt: string | null;
  resolvedAt: string | null;
  photos?: ClaimPhoto[];
}

interface ClaimPhoto {
  id: string;
  claimId: string;
  objectStorageKey: string;
  originalFilename: string | null;
  caption: string | null;
}

interface UserOrder {
  id: string;
  displayOrderNumber: string;
  status: string;
  totalAmount: string;
  createdAt: string;
}

const claimFormSchema = z.object({
  orderId: z.string().min(1, "Please select an order"),
  issueType: z.enum(["DAMAGED", "WRONG_ITEM", "MISSING_PARTS", "NOT_AS_DESCRIBED", "DOA", "OTHER"]),
  description: z.string().min(10, "Please provide more detail about the issue (at least 10 characters)"),
});

type ClaimFormValues = z.infer<typeof claimFormSchema>;

const issueTypeLabels: Record<string, string> = {
  DAMAGED: "Item Damaged",
  WRONG_ITEM: "Wrong Item Received",
  MISSING_PARTS: "Missing Parts",
  NOT_AS_DESCRIBED: "Not As Described",
  DOA: "Dead on Arrival",
  OTHER: "Other Issue",
};

const statusLabels: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
  SUBMITTED: { label: "Submitted", variant: "secondary" },
  UNDER_REVIEW: { label: "Under Review", variant: "default" },
  APPROVED: { label: "Approved", variant: "default" },
  DENIED: { label: "Denied", variant: "destructive" },
  RESOLVED: { label: "Resolved", variant: "default" },
  CANCELLED: { label: "Cancelled", variant: "outline" },
};

export function CustomerClaimsSection() {
  const { toast } = useToast();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [pendingPhotos, setPendingPhotos] = useState<PendingPhoto[]>([]);
  const [isUploadingPhotos, setIsUploadingPhotos] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: claims, isLoading: claimsLoading } = useQuery<Claim[]>({
    queryKey: ["/api/my/claims"],
  });

  const { data: orders, isLoading: ordersLoading } = useQuery<UserOrder[]>({
    queryKey: ["/api/my/orders"],
  });

  const form = useForm<ClaimFormValues>({
    resolver: zodResolver(claimFormSchema),
    defaultValues: {
      orderId: "",
      issueType: "DAMAGED",
      description: "",
    },
  });

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const newPhotos: PendingPhoto[] = [];
    for (let i = 0; i < files.length && pendingPhotos.length + newPhotos.length < 5; i++) {
      const file = files[i];
      if (file.type.startsWith('image/')) {
        newPhotos.push({
          file,
          preview: URL.createObjectURL(file),
        });
      }
    }

    setPendingPhotos(prev => [...prev, ...newPhotos].slice(0, 5));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removePhoto = (index: number) => {
    setPendingPhotos(prev => {
      const updated = [...prev];
      URL.revokeObjectURL(updated[index].preview);
      updated.splice(index, 1);
      return updated;
    });
  };

  const uploadPhotosToClaimInternal = async (claimId: string) => {
    for (const photo of pendingPhotos) {
      const formData = new FormData();
      formData.append('file', photo.file);
      
      const uploadResponse = await fetch('/api/object-storage/upload', {
        method: 'POST',
        body: formData,
      });

      if (uploadResponse.ok) {
        const { key } = await uploadResponse.json();
        await apiRequest('POST', `/api/my/claims/${claimId}/photos`, {
          objectStorageKey: key,
          originalFilename: photo.file.name,
          mimeType: photo.file.type,
          fileSize: photo.file.size,
        });
      }
    }
  };

  const createClaimMutation = useMutation({
    mutationFn: async (data: ClaimFormValues) => {
      const claim = await apiRequest('POST', '/api/my/claims', data);
      
      if (pendingPhotos.length > 0 && claim.id) {
        setIsUploadingPhotos(true);
        try {
          await uploadPhotosToClaimInternal(claim.id);
        } catch (error) {
          console.error('Photo upload error:', error);
        } finally {
          setIsUploadingPhotos(false);
        }
      }
      
      return claim;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/my/claims"] });
      toast({
        title: "Claim submitted",
        description: "Your claim has been submitted and is being reviewed.",
      });
      setCreateDialogOpen(false);
      form.reset();
      pendingPhotos.forEach(p => URL.revokeObjectURL(p.preview));
      setPendingPhotos([]);
    },
    onError: (error: any) => {
      toast({
        title: "Failed to submit claim",
        description: error.message || "Please try again",
        variant: "destructive",
      });
    },
  });

  const cancelClaimMutation = useMutation({
    mutationFn: async (claimId: string) => {
      return await apiRequest('PATCH', `/api/my/claims/${claimId}/cancel`, {});
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/my/claims"] });
      toast({
        title: "Claim cancelled",
        description: "Your claim has been cancelled.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to cancel claim",
        description: error.message || "Please try again",
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: ClaimFormValues) => {
    createClaimMutation.mutate(data);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  if (claimsLoading || ordersLoading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="animate-pulse h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="animate-pulse h-10 bg-gray-200 rounded w-32"></div>
        </div>
        {[1, 2, 3].map((i) => (
          <div key={i} className="animate-pulse h-32 bg-gray-200 rounded"></div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-lg sm:text-xl font-bold text-foreground">Returns & Claims</h2>
          <p className="text-xs sm:text-sm text-muted-foreground">Submit and track your return requests</p>
        </div>
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="button-new-claim" className="w-full sm:w-auto">
              <Plus className="mr-2 h-4 w-4" />
              Submit New Claim
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[500px]" data-testid="dialog-new-claim">
            <DialogHeader>
              <DialogTitle>Submit a Claim</DialogTitle>
            </DialogHeader>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="orderId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Select Order *</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger data-testid="select-claim-order">
                            <SelectValue placeholder="Select an order" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {orders && orders.length > 0 ? (
                            orders.map((order) => (
                              <SelectItem 
                                key={order.id} 
                                value={order.id}
                                data-testid={`option-order-${order.id}`}
                              >
                                Order #{order.displayOrderNumber} - {formatDate(order.createdAt)}
                              </SelectItem>
                            ))
                          ) : (
                            <SelectItem value="" disabled>No orders found</SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="issueType"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Issue Type *</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger data-testid="select-issue-type">
                            <SelectValue placeholder="Select issue type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {Object.entries(issueTypeLabels).map(([value, label]) => (
                            <SelectItem key={value} value={value} data-testid={`option-issue-${value}`}>
                              {label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Description *</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Please describe the issue in detail..."
                          className="min-h-[100px]"
                          data-testid="input-claim-description"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                {/* Photo Upload Section */}
                <div className="space-y-2">
                  <FormLabel>Photos (Optional)</FormLabel>
                  <p className="text-xs text-muted-foreground">
                    Upload up to 5 photos of the issue to help us process your claim faster.
                  </p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleFileSelect}
                    className="hidden"
                    data-testid="input-claim-photos"
                  />
                  <div className="flex flex-wrap gap-2">
                    {pendingPhotos.map((photo, index) => (
                      <div key={index} className="relative group">
                        <img
                          src={photo.preview}
                          alt={`Claim photo ${index + 1}`}
                          className="w-16 h-16 object-cover rounded border"
                        />
                        <button
                          type="button"
                          onClick={() => removePhoto(index)}
                          className="absolute -top-2 -right-2 bg-destructive text-destructive-foreground rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                          data-testid={`button-remove-photo-${index}`}
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                    {pendingPhotos.length < 5 && (
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="w-16 h-16 border-2 border-dashed rounded flex items-center justify-center text-muted-foreground hover:border-primary hover:text-primary transition-colors"
                        data-testid="button-add-photo"
                      >
                        <ImagePlus className="h-5 w-5" />
                      </button>
                    )}
                  </div>
                </div>

                <DialogFooter className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setCreateDialogOpen(false)}
                    data-testid="button-cancel-claim"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={createClaimMutation.isPending || isUploadingPhotos}
                    data-testid="button-submit-claim"
                  >
                    {createClaimMutation.isPending || isUploadingPhotos ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {isUploadingPhotos ? "Uploading photos..." : "Submitting..."}
                      </>
                    ) : (
                      "Submit Claim"
                    )}
                  </Button>
                </DialogFooter>
              </form>
            </Form>
          </DialogContent>
        </Dialog>
      </div>

      {!claims || claims.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <ClipboardList className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No claims yet</h3>
            <p className="text-sm text-muted-foreground mb-4 text-center max-w-sm">
              If you have an issue with an order, you can submit a claim here and we'll help resolve it.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {claims.map((claim) => {
            const statusInfo = statusLabels[claim.status] || { label: claim.status, variant: "secondary" as const };
            return (
              <Card key={claim.id} data-testid={`card-claim-${claim.id}`}>
                <CardContent className="pt-6">
                  <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                    <div className="space-y-2 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold" data-testid={`text-claim-number-${claim.id}`}>
                          {claim.claimNumber}
                        </span>
                        <Badge variant={statusInfo.variant} data-testid={`badge-claim-status-${claim.id}`}>
                          {statusInfo.label}
                        </Badge>
                        <Badge variant="outline" data-testid={`badge-claim-type-${claim.id}`}>
                          {issueTypeLabels[claim.issueType] || claim.issueType}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground" data-testid={`text-claim-description-${claim.id}`}>
                        {claim.description}
                      </p>
                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          Submitted {formatDate(claim.createdAt)}
                        </span>
                        {claim.resolvedAt && (
                          <span>Resolved {formatDate(claim.resolvedAt)}</span>
                        )}
                      </div>
                      {claim.resolutionNotes && (
                        <div className="mt-3 p-3 bg-muted rounded-lg">
                          <p className="text-xs font-medium text-muted-foreground mb-1">Resolution Notes</p>
                          <p className="text-sm">{claim.resolutionNotes}</p>
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2">
                      {claim.status === "SUBMITTED" && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => cancelClaimMutation.mutate(claim.id)}
                          disabled={cancelClaimMutation.isPending}
                          data-testid={`button-cancel-claim-${claim.id}`}
                        >
                          Cancel Claim
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
